// src/pages/Reports/AddDataArchive/AddDataArchiveUI.jsx

import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Grid, Paper, Typography, Chip, Tooltip, Skeleton, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Box} from '@mui/material';
import AppComponentHeader from '../../../@crema/core/AppComponentHeader';
import AppGridContainer from '../../../@crema/core/AppGridContainer';
import DataArchiveFilterCard from 'components/FilterCard/DataArchiveFilterCard';
import FilterListIcon from '@mui/icons-material/FilterList';
import {DataGrid} from '@mui/x-data-grid';
import { formatDateToJerusalem } from '../../../utils/dateFormatter';

const AddDataArchiveUI = ({
  dataArchive,
  archiveCount,
  page,
  rowsPerPage,
  loading,
  onPageChange,
  onPageSizeChange,
  filters,
  setFilters,
  handleDateRangeChange,
  onApply,
  onClear,
  handleGenerateReport,
}) => {
  const [isFilterCardVisible, setIsFilterCardVisible] = useState(false);

  const toggleFilterCardVisibility = () => {
    setIsFilterCardVisible(!isFilterCardVisible);
  };

  const columns = [
    {
      field: 'rowNumber',
      headerName: 'Row Number',
      width: 120,
      sortable: false,
      filterable: false,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },
    {
      field: 'name',
      headerName: 'Report Name',
      width: 250,
      flex: 1,
    },
    {
      field: 'uploaded_at',
      headerName: 'Save Date',
      width: 200,
      type: 'date',
      valueGetter: (params) => new Date(params.row.uploaded_at),
      valueFormatter: (params) => formatDateToJerusalem(params.value, 'date-only'),
    },
    {
      field: 'archive_count',
      headerName: 'Data Count',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Chip
          label={`${params.row.archive_count || 0} records`}
          color={params.row.archive_count > 0 ? 'success' : 'default'}
          size="small"
          variant={params.row.archive_count > 0 ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'generateReport',
      headerName: 'Generate Report',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const hasData = params.row.archive_count > 0;
        return (
          <Tooltip 
            title={hasData ? "Generate Excel report" : "No data available to export"} 
            arrow
          >
            <span>
              <Button
                variant='contained'
                color={hasData ? 'primary' : 'secondary'}
                disabled={!hasData}
                onClick={() => handleGenerateReport(params.row.report_id)}
                sx={{ 
                  opacity: hasData ? 1 : 0.6,
                  cursor: hasData ? 'pointer' : 'not-allowed',
                  backgroundColor: !hasData ? '#f5f5f5' : undefined,
                  color: !hasData ? '#666' : undefined,
                  '&:hover': {
                    backgroundColor: !hasData ? '#f5f5f5' : undefined,
                  }
                }}
              >
                {hasData ? 'Generate' : 'No Data'}
              </Button>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <AppComponentHeader title='Data Table' />
      <AppGridContainer>
        <Grid item xs={12}>
          <Button
            variant='contained'
            startIcon={<FilterListIcon />}
            onClick={toggleFilterCardVisibility}
          >
            {isFilterCardVisible ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </Grid>

        {isFilterCardVisible && (
          <Grid item xs={12}>
            <DataArchiveFilterCard
              filters={filters}
              setFilters={setFilters}
              handleDateRangeChange={handleDateRangeChange}
              onApply={onApply}
              onClear={onClear}
              disableDataSource={false}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Paper sx={{padding: 2, marginTop: 4}}>
            <Typography variant='h6' sx={{mb: 2}}>
              Data Archive List
            </Typography>
            {loading ? (
              <TableContainer 
                component={Paper}
                sx={{
                  maxHeight: 600,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '& .MuiTable-root': {
                    minWidth: 650,
                  },
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow sx={{'& th': {fontWeight: 600, backgroundColor: 'background.paper'}}}>
                      {columns.map((column) => (
                        <TableCell 
                          key={column.field} 
                          sx={{
                            width: column.width || 'auto',
                            minWidth: column.width || 'auto',
                            backgroundColor: 'background.paper',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {column.headerName}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.from({length: Math.min(rowsPerPage, 15)}).map((_, index) => (
                      <TableRow 
                        key={`skeleton-${index}`}
                        sx={{
                          '&:nth-of-type(even)': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        {columns.map((column) => (
                          <TableCell 
                            key={`skeleton-${index}-${column.field}`} 
                            sx={{
                              py: 1.5,
                              px: 2,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Skeleton 
                              variant='rectangular' 
                              height={20} 
                              sx={{
                                borderRadius: 0.5,
                                width: column.width ? '100%' : '80%',
                              }} 
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Box 
                  sx={{
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    p: 2, 
                    borderTop: 1, 
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Skeleton variant='text' width={120} height={20} />
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                    <Skeleton variant='text' width={100} height={20} />
                    <Skeleton variant='rectangular' width={200} height={36} sx={{borderRadius: 1}} />
                  </Box>
                </Box>
              </TableContainer>
            ) : (
            <DataGrid
              rows={dataArchive}
              columns={columns}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                pagination
              paginationMode='server'
              rowCount={archiveCount}
              onPageChange={(newPage) => onPageChange(null, newPage)}
                onPageSizeChange={(newPageSize) => onPageSizeChange(null, newPageSize)}
                page={page}
              disableSelectionOnClick
              autoHeight
                getRowId={(row) => row.id || row.report_id}
            />
            )}
          </Paper>
        </Grid>
      </AppGridContainer>
    </>
  );
};

AddDataArchiveUI.propTypes = {
  dataArchive: PropTypes.array.isRequired,
  archiveCount: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  handleDateRangeChange: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  handleGenerateReport: PropTypes.func.isRequired,
};

export default AddDataArchiveUI;
